const React = require('react');
const { createElement: h, useState, useEffect } = React;
const templateStore = require('../lib/templateStore');
const { renderChatMarkdown } = require('../lib/renderChatMarkdown');
const NodeIcon = require('./NodeIcon');
const WorkflowPreviewGraph = require('./WorkflowPreviewGraph');
require('./TemplateStorePage.css');

// Premium single-hue brand washes (top→bottom). One hue per gradient — far
// calmer than the old rainbow palette — so the hero reads like a polished brand
// surface rather than a default wallpaper. Picked deterministically from the
// category seed so every template in a category shares its visual family.
var GRADIENTS = [
  'linear-gradient(180deg, #3b82f6 0%, #1e3a8a 100%)', // azure
  'linear-gradient(180deg, #7c3aed 0%, #3b1380 100%)', // violet
  'linear-gradient(180deg, #0ea5b7 0%, #0b4f5a 100%)', // teal
  'linear-gradient(180deg, #ef4d6a 0%, #7a142a 100%)', // rose
  'linear-gradient(180deg, #f59e0b 0%, #7a3e05 100%)', // amber
  'linear-gradient(180deg, #16a34a 0%, #0a4926 100%)', // emerald
  'linear-gradient(180deg, #475569 0%, #1e293b 100%)', // slate
  'linear-gradient(180deg, #db2777 0%, #6b1244 100%)'  // magenta
];
function gradientFor(seed) {
  var s = String(seed || '');
  var sum = 0;
  for (var i = 0; i < s.length; i++) sum += s.charCodeAt(i);
  return GRADIENTS[sum % GRADIENTS.length];
}

// Turn any YouTube URL (watch / youtu.be / shorts / embed) into an embed URL.
function youtubeEmbedUrl(url) {
  if (!url) return '';
  var m = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? 'https://www.youtube.com/embed/' + m[1] : '';
}

function coverStyle(template) {
  if (template && template.coverImage) {
    return { backgroundImage: 'url("' + template.coverImage + '")', backgroundSize: 'cover', backgroundPosition: 'center' };
  }
  return { backgroundImage: gradientFor(template && (template.category || template.title)) };
}

// Deterministic brand-ish color for an app chip, picked from the service name so
// the same connector always reads the same color across cards.
var CHIP_COLORS = [
  '#1f2937', // ink
  '#2dbd8e', // green
  '#3b1d52', // plum
  '#0a0a0a', // black
  '#d9534f', // red
  '#3d6df0', // blue
  '#7c5cf0', // violet
  '#e0a32e'  // amber
];
function chipColorFor(seed) {
  var s = String(seed || '');
  var sum = 0;
  for (var i = 0; i < s.length; i++) sum += s.charCodeAt(i);
  return CHIP_COLORS[sum % CHIP_COLORS.length];
}

// First letter of a name, upper-cased — the fallback glyph for a chip with no
// real logo image (e.g. an agent node, or a template with only connector names).
function chipLetter(name) {
  return String(name || '?').replace(/[^A-Za-z0-9]/g, '').charAt(0).toUpperCase() || '?';
}

// The distinct app-icon chips for a card: the workflow's real service logos
// (deduped by logoUrl so distinct Google products don't collapse), falling back
// to a colored letter chip from a connector name when no logos are present.
function appChips(template) {
  var chips = [];
  var seen = {};
  (Array.isArray(template.logos) ? template.logos : []).forEach(function(l) {
    if (!l) return;
    var key = l.logoUrl || ('icon:' + (l.icon || ''));
    if (seen[key]) return;
    seen[key] = true;
    chips.push({
      logoUrl: l.logoUrl || '',
      isAgent: !!l.isAgent,
      icon: l.icon || '',
      seed: l.logoUrl || l.icon || ''
    });
  });
  if (!chips.length) {
    (Array.isArray(template.connectors) ? template.connectors : []).forEach(function(c) {
      if (!c) return;
      var key = String(c).toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      chips.push({ logoUrl: '', seed: String(c) });
    });
  }
  return chips;
}

// Render one chip's inner content (logo img, agent icon, or letter glyph). The
// outer <span> wrapper is supplied by the caller so a single slot can swap its
// contents while keeping a stable element (used by the cycling overflow tile).
function chipInner(chip, agentSize) {
  if (chip.logoUrl) {
    return h('img', {
      className: 'ts-chip-img',
      src: chip.logoUrl,
      alt: '',
      onError: function(e) { e.target.style.display = 'none'; }
    });
  }
  if (chip.isAgent) {
    return h(NodeIcon, { type: chip.icon || 'agent', size: agentSize });
  }
  return chipLetter(chip.seed);
}

// Class + title for a chip's wrapper <span>, by kind. iconVar carries the
// per-variant modifier (e.g. ts-chip-icon--row).
function chipWrap(chip, iconVar) {
  if (chip.logoUrl) {
    return { className: 'ts-chip-icon ts-chip-icon--logo' + iconVar, title: chip.seed };
  }
  if (chip.isAgent) {
    return { className: 'ts-chip-icon ts-chip-icon--logo ts-chip-icon--agent' + iconVar, title: 'AI agent' };
  }
  return { className: 'ts-chip-icon' + iconVar, style: { background: chipColorFor(chip.seed) }, title: chip.seed };
}

// The last visible tile when a card has more icons than fit: it cross-fades
// through the overflow chips on a 2s loop, like an animated app icon. `chips`
// is the full overflow set (the tiles that wouldn't otherwise be shown).
function CyclingChip(props) {
  var chips = props.chips;
  var [idx, setIdx] = useState(0);
  useEffect(function() {
    if (chips.length <= 1) return undefined;
    var timer = setInterval(function() {
      setIdx(function(i) { return (i + 1) % chips.length; });
    }, 2000);
    return function() { clearInterval(timer); };
  }, [chips.length]);
  var chip = chips[idx % chips.length];
  var wrap = chipWrap(chip, props.iconVar);
  // key forces a remount on swap so the fade animation replays each cycle.
  return h('span', wrap, h('span', { key: idx, className: 'ts-chip-cycle' }, chipInner(chip, props.agentSize)));
}

function AppChips(props) {
  var chips = appChips(props.template);
  var max = props.max || 4;
  if (!chips.length) chips = [{ logoUrl: '', seed: String(props.template.title || '?') }];
  // variant: '' (card), 'feat' (big white tiles), 'row' (overlapping list tiles)
  var variant = props.variant ? (' ts-chips-row--' + props.variant) : '';
  var iconVar = props.variant ? (' ts-chip-icon--' + props.variant) : '';
  var agentSize = props.variant === 'feat' ? 30 : (props.variant === 'row' ? 28 : 22);

  // When there are more chips than slots, the final slot cycles through the
  // remaining ones so every service still gets shown over time.
  var hasOverflow = chips.length > max;
  var leading = chips.slice(0, hasOverflow ? max - 1 : max);
  var overflow = hasOverflow ? chips.slice(max - 1) : [];

  return h('div', { className: 'ts-chips-row' + variant },
    leading.map(function(chip, i) {
      return h('span', Object.assign({ key: i }, chipWrap(chip, iconVar)), chipInner(chip, agentSize));
    }).concat(hasOverflow
      ? [h(CyclingChip, { key: 'cycle', chips: overflow, iconVar: iconVar, agentSize: agentSize })]
      : [])
  );
}

// "App icon" square. If the workflow uses service logos, cycle through them —
// one every 2s, auto-repeating — like an animated app icon. Otherwise fall back
// to the cover image, or a gradient with the title's initial.
function AppIcon(props) {
  var t = props.template;
  var feat = !!props.feat;
  var size = feat ? 40 : (props.large ? 44 : (props.card ? 34 : 26));
  var logos = (Array.isArray(t.logos) ? t.logos : []).filter(function(l) { return l && (l.logoUrl || l.isAgent); });
  var [idx, setIdx] = useState(0);

  useEffect(function() {
    if (logos.length <= 1) return undefined;
    var timer = setInterval(function() {
      setIdx(function(i) { return (i + 1) % logos.length; });
    }, 2000);
    return function() { clearInterval(timer); };
  }, [logos.length]);

  var tileClass = 'ts-icon ts-icon--logo' + (feat ? ' ts-icon--feat' : '');
  if (logos.length) {
    var cur = logos[idx % logos.length];
    return h('div', { className: tileClass },
      cur.logoUrl
        ? h('img', { key: cur.logoUrl, className: 'ts-icon-img', src: cur.logoUrl, alt: '', onError: function(e) { e.target.style.visibility = 'hidden'; } })
        : h('span', { key: 'agent', className: 'ts-icon-agent' }, h(NodeIcon, { type: cur.icon, size: size }))
    );
  }

  var letter = String(t.title || '?').trim().charAt(0).toUpperCase();
  return h('div', { className: 'ts-icon' + (feat ? ' ts-icon--feat' : ''), style: coverStyle(t) },
    t.coverImage ? null : h('span', { className: 'ts-icon-letter' }, letter)
  );
}

// Compact install count for the card footer (e.g. 2300 → "2.3k").
function shortInstalls(n) {
  var v = Number(n) || 0;
  if (v >= 1000) {
    var k = v / 1000;
    return (k >= 10 ? Math.round(k) : Math.round(k * 10) / 10) + 'k';
  }
  return String(v);
}

// Row meta line — "N installs · Category · Free".
function cardMeta(t) {
  var parts = [];
  var n = Number(t.installCount) || 0;
  if (n) parts.push(shortInstalls(n) + (n === 1 ? ' install' : ' installs'));
  if (t.category) parts.push(t.category);
  parts.push('Free');
  return parts.join(' · ');
}

// Featured banner — the most-installed template as a large dark hero card with a
// FEATURED pill, title, description, an Install button, and big app-icon tiles.
function FeaturedBanner(props) {
  var t = props.template;
  var n = Number(t.installCount) || 0;
  return h('div', { className: 'ts-feat', onClick: function() { props.onOpen(t.id); } },
    h('div', { className: 'ts-feat-main' },
      h('div', { className: 'ts-feat-top' },
        h('span', { className: 'ts-feat-pill' }, 'Featured'),
        n ? h('span', { className: 'ts-feat-installs' }, shortInstalls(n) + ' installs') : null
      ),
      h('div', { className: 'ts-feat-title' }, t.title),
      t.summary ? h('div', { className: 'ts-feat-desc' }, t.summary) : null,
      h('div', { className: 'ts-feat-actions' },
        h('button', {
          type: 'button',
          className: 'ts-feat-btn',
          onClick: function(e) { e.stopPropagation(); props.onOpen(t.id); }
        }, 'Install template'),
        h('span', { className: 'ts-feat-meta' }, (t.category || 'Workflow') + ' · Free')
      )
    ),
    h('div', { className: 'ts-feat-icons' }, h(AppIcon, { template: t, feat: true }))
  );
}

// Compact list row: 2 overlapping app-icon tiles, title + meta, and a → arrow.
function TemplateRow(props) {
  var t = props.template;
  return h('div', { className: 'ts-row', onClick: function() { props.onOpen(t.id); } },
    h(AppChips, { template: t, variant: 'row', max: 2 }),
    h('div', { className: 'ts-row-info' },
      h('div', { className: 'ts-row-title' }, t.title),
      h('div', { className: 'ts-row-meta' }, cardMeta(t))
    ),
    h('span', { className: 'ts-row-arrow', 'aria-hidden': true }, '→')
  );
}

// ── Detail view ─────────────────────────────────────────────────────────────

// "How it works" steps derived from the workflow's nodes (flow order). Title =
// node title, description = its subtitle. Start/system nodes are skipped so the
// list reads like real work the workflow does.
function stepsFromGraph(graph) {
  if (!graph || !Array.isArray(graph.nodes)) return [];
  return graph.nodes
    .filter(function(n) { return n && n.icon !== 'start' && n.title; })
    .map(function(n) {
      return { title: String(n.title), desc: String(n.subtitle || '') };
    });
}

// Integrations list for the sidebar: one entry per distinct service logo (real
// brand image) plus any connector names without a logo. Name only, no role.
function integrationsFor(template) {
  var out = [];
  var seen = {};
  (Array.isArray(template.logos) ? template.logos : []).forEach(function(l) {
    if (!l) return;
    var key = l.logoUrl || ('icon:' + (l.icon || ''));
    if (seen[key]) return;
    seen[key] = true;
    out.push({ logoUrl: l.logoUrl || '', isAgent: !!l.isAgent, icon: l.icon || '', name: '' });
  });
  // Pair connector names onto logo entries by order, and add any extras.
  var names = (Array.isArray(template.connectors) ? template.connectors : []).slice();
  for (var i = 0; i < out.length; i += 1) {
    if (!out[i].name && names.length) out[i].name = names.shift();
  }
  names.forEach(function(nm) { if (nm) out.push({ logoUrl: '', isAgent: false, icon: '', name: String(nm) }); });
  return out;
}

function IntegrationRow(props) {
  var it = props.item;
  var icon;
  if (it.logoUrl) {
    icon = h('img', { className: 'ts-integ-img', src: it.logoUrl, alt: '', onError: function(e) { e.target.style.display = 'none'; } });
  } else if (it.isAgent) {
    icon = h('span', { className: 'ts-integ-agent' }, h(NodeIcon, { type: it.icon || 'agent', size: 20 }));
  } else {
    icon = h('span', { className: 'ts-integ-letter' }, chipLetter(it.name));
  }
  return h('div', { className: 'ts-integ-row' },
    h('span', { className: 'ts-integ-icon' }, icon),
    h('span', { className: 'ts-integ-name' }, it.name || 'Service')
  );
}

// Short "Jun 2026" style label from an ISO date.
var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function shortDate(iso) {
  if (!iso) return '—';
  var d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return MONTHS[d.getMonth()] + ' ' + d.getFullYear();
}

function StoreDetail(props) {
  var [template, setTemplate] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [installing, setInstalling] = useState(false);

  useEffect(function() {
    var alive = true;
    setLoading(true);
    setError('');
    templateStore.getStoreTemplate(props.templateId)
      .then(function(t) { if (alive) { setTemplate(t); setLoading(false); } })
      .catch(function(e) { if (alive) { setError(e.message || 'Failed to load template.'); setLoading(false); } });
    return function() { alive = false; };
  }, [props.templateId]);

  function handleUse() {
    if (!props.currentUser) { props.onNavigateTo('/login'); return; }
    setInstalling(true);
    setError('');
    templateStore.installTemplate(props.templateId)
      .then(function(created) { props.onInstalled(created && created.id); })
      .catch(function(e) { setInstalling(false); setError(e.message || 'Install failed.'); });
  }

  if (loading) return h('div', { className: 'ts-state' }, 'Loading template…');
  if (error && !template) return h('div', { className: 'ts-state ts-state--error' }, error);
  if (!template) return h('div', { className: 'ts-state' }, 'Template not found.');

  var installs = template.installCount || 0;
  var connectors = Array.isArray(template.connectors) ? template.connectors : [];
  var headerTags = [template.category, template.subcategory].filter(Boolean);
  var demoEmbed = youtubeEmbedUrl(template.videoUrl);
  var steps = stepsFromGraph(template.previewGraph);
  var integrations = integrationsFor(template);
  var installLabel = installs.toLocaleString() + (installs === 1 ? ' install' : ' installs');
  var hasPreview = template.previewGraph && Array.isArray(template.previewGraph.nodes) && template.previewGraph.nodes.length;

  function getBtn(cls) {
    return h('button', { type: 'button', className: 'ts-get ' + cls, disabled: installing, onClick: handleUse },
      installing ? 'Installing…' : (props.currentUser ? 'Get template' : 'Sign in to install'));
  }

  return h('div', { className: 'ts-detail' },
    h('div', { className: 'ts-detail-body' },
      // Breadcrumb
      h('div', { className: 'ts-crumb' },
        h('button', { type: 'button', className: 'ts-crumb-link', onClick: props.onBack }, '← Template store'),
        h('span', { className: 'ts-crumb-sep' }, '/'),
        h('span', { className: 'ts-crumb-here' }, template.title)
      ),

      // ── Header: icon + title + meta + tags (left) | Get template (right) ──
      h('header', { className: 'ts-dhead' },
        h('div', { className: 'ts-dhead-left' },
          h('div', { className: 'ts-dhead-top' },
            h(AppIcon, { template: template, large: true }),
            h('div', { className: 'ts-dhead-titles' },
              h('h1', { className: 'ts-product-name' }, template.title),
              h('div', { className: 'ts-dhead-sub' },
                template.author ? h('span', { className: 'ts-product-by' },
                  h('span', { className: 'ts-product-by-check', 'aria-hidden': true }, '✓'), 'by ' + template.author) : null,
                installs > 0 ? h('span', { className: 'ts-dot-sep' }, '·') : null,
                installs > 0 ? h('span', { className: 'ts-product-installs' }, installLabel) : null
              )
            )
          ),
          (headerTags.length || connectors.length)
            ? h('div', { className: 'ts-product-tags' },
                headerTags.concat(connectors).map(function(tag, i) {
                  return h('span', { key: i, className: 'ts-chip' }, tag);
                })
              )
            : null
        ),
        h('div', { className: 'ts-dhead-cta' },
          getBtn('ts-get--hero'),
          h('div', { className: 'ts-get-note' }, props.currentUser ? 'Free · installs in one click' : 'Free · sign in to install')
        )
      ),

      error ? h('div', { className: 'ts-inline-error' }, error) : null,

      // ── Workflow preview (unchanged renderer) ──
      hasPreview
        ? h('section', { className: 'ts-section ts-section--preview' },
            h('div', { className: 'ts-shot' },
              h(WorkflowPreviewGraph, { graph: template.previewGraph })
            )
          )
        : template.screenshot
          ? h('section', { className: 'ts-section ts-section--preview' },
              h('div', { className: 'ts-shot' },
                h('img', { className: 'ts-shot-img', src: template.screenshot, alt: 'Workflow canvas preview' })
              )
            )
          : null,

      // ── Two-column body: content (left) + sticky sidebar (right) ──
      h('div', { className: 'ts-dcols' },
        h('div', { className: 'ts-dmain' },
          template.summary
            ? h('section', { className: 'ts-section' },
                h('h2', { className: 'ts-section-h' }, 'Overview'),
                h('p', { className: 'ts-section-p' }, template.summary)
              )
            : null,

          steps.length
            ? h('section', { className: 'ts-section' },
                h('h2', { className: 'ts-section-h' }, 'How it works'),
                h('div', { className: 'ts-steps' },
                  steps.map(function(s, i) {
                    return h('div', { key: i, className: 'ts-step' },
                      h('span', { className: 'ts-step-num' }, String(i + 1)),
                      h('div', { className: 'ts-step-body' },
                        h('div', { className: 'ts-step-title' }, s.title),
                        s.desc ? h('div', { className: 'ts-step-desc' }, s.desc) : null
                      )
                    );
                  })
                )
              )
            : null,

          template.readme
            ? h('section', { className: 'ts-section' },
                h('h2', { className: 'ts-section-h' }, 'Before you run it'),
                h('div', { className: 'ts-readme-body' }, renderChatMarkdown(template.readme, 'ts'))
              )
            : null,

          demoEmbed
            ? h('section', { className: 'ts-section' },
                h('h2', { className: 'ts-section-h' }, 'Demo'),
                h('div', { className: 'ts-video' },
                  h('iframe', {
                    className: 'ts-video-frame',
                    src: demoEmbed,
                    title: 'Demo video',
                    frameBorder: '0',
                    allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
                    allowFullScreen: true
                  })
                )
              )
            : null
        ),

        // ── Sidebar ──
        h('aside', { className: 'ts-dside' },
          // Integrations card
          integrations.length
            ? h('div', { className: 'ts-side-card' },
                h('div', { className: 'ts-side-label' }, 'Integrations'),
                h('div', { className: 'ts-integ-list' },
                  integrations.map(function(it, i) { return h(IntegrationRow, { key: i, item: it }); })
                )
              )
            : null,

          // Meta card
          h('div', { className: 'ts-side-card ts-side-meta' },
            h('div', { className: 'ts-meta-row' }, h('span', { className: 'ts-meta-k' }, 'Author'), h('span', { className: 'ts-meta-v' }, template.author || '—')),
            h('div', { className: 'ts-meta-row' }, h('span', { className: 'ts-meta-k' }, 'Category'), h('span', { className: 'ts-meta-v' }, template.subcategory || template.category || '—')),
            h('div', { className: 'ts-meta-row' }, h('span', { className: 'ts-meta-k' }, 'Nodes'), h('span', { className: 'ts-meta-v' }, String(template.nodeCount || 0))),
            h('div', { className: 'ts-meta-row' }, h('span', { className: 'ts-meta-k' }, 'Updated'), h('span', { className: 'ts-meta-v' }, shortDate(template.updatedAt)))
          )
        )
      )
    ) // end .ts-detail-body
  );
}

// ── Grid view ───────────────────────────────────────────────────────────────

function StoreGrid(props) {
  var [templates, setTemplates] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [activeCategory, setActiveCategory] = useState('All');
  var [search, setSearch] = useState('');
  var [sort, setSort] = useState('popular');

  useEffect(function() {
    var alive = true;
    templateStore.listStoreTemplates()
      .then(function(list) { if (alive) { setTemplates(list || []); setLoading(false); } })
      .catch(function(e) { if (alive) { setError(e.message || 'Failed to load templates.'); setLoading(false); } });
    return function() { alive = false; };
  }, []);

  var categories = ['All'].concat(templates.reduce(function(acc, t) {
    if (t.category && acc.indexOf(t.category) === -1) acc.push(t.category);
    return acc;
  }, []));

  // Combined filter: category chip AND free-text search across the fields a
  // visitor would naturally search by (title, summary, category, author, and
  // any of the connector names like "Gmail", "Slack").
  var query = search.trim().toLowerCase();
  var visible = templates.filter(function(t) {
    if (activeCategory !== 'All' && t.category !== activeCategory) return false;
    if (!query) return true;
    var hay = [t.title, t.summary, t.category, t.subcategory, t.author]
      .concat(Array.isArray(t.connectors) ? t.connectors : [])
      .filter(Boolean).join(' ').toLowerCase();
    return hay.indexOf(query) !== -1;
  }).slice().sort(function(a, b) {
    if (sort === 'az') return String(a.title || '').localeCompare(String(b.title || ''));
    if (sort === 'newest') return String(b.createdAt || b.id || '').localeCompare(String(a.createdAt || a.id || ''));
    // popular (default): most installs first.
    return (Number(b.installCount) || 0) - (Number(a.installCount) || 0);
  });
  var emptyMessage = query
    ? 'No templates match “' + search.trim() + '”.'
    : 'No templates published yet. Check back soon.';

  // Featured = the template an admin flagged (template.featured). If none is
  // flagged, fall back to the most-installed so the banner is never empty. The
  // featured template is shown ONLY in the banner, not repeated in the list.
  var featured = templates.filter(function(t) { return t.featured; })[0]
    || templates.slice().sort(function(a, b) { return (Number(b.installCount) || 0) - (Number(a.installCount) || 0); })[0]
    || null;
  // Only show the banner on the default view (no active filter/search).
  var showFeatured = featured && activeCategory === 'All' && !query;
  var rows = showFeatured ? visible.filter(function(t) { return t.id !== featured.id; }) : visible;

  return h('div', { className: 'ts-grid-wrap' },
    h('header', { className: 'ts-hero' },
      h('div', { className: 'ts-hero-eyebrow' }, 'Template Store'),
      h('h1', { className: 'ts-hero-title' }, 'Ship automations, not setup time.')
    ),
    showFeatured ? h('div', { className: 'ts-feat-wrap' },
      h(FeaturedBanner, { template: featured, onOpen: props.onOpen })
    ) : null,
    // ── Filter row: category pills (left) + search + sort (right) ──
    h('div', { className: 'ts-toolbar' },
      categories.length > 1
        ? h('div', { className: 'ts-cats' },
            categories.map(function(cat) {
              return h('button', {
                key: cat,
                type: 'button',
                className: 'ts-cat' + (cat === activeCategory ? ' ts-cat--active' : ''),
                onClick: function() { setActiveCategory(cat); }
              }, cat);
            })
          )
        : h('div', null),
      h('div', { className: 'ts-toolbar-right' },
        h('div', { className: 'ts-search' },
          h('svg', {
            className: 'ts-search-icon',
            width: 16, height: 16, viewBox: '0 0 24 24',
            fill: 'none', stroke: 'currentColor', strokeWidth: 2,
            strokeLinecap: 'round', strokeLinejoin: 'round',
            'aria-hidden': true
          },
            h('circle', { cx: 11, cy: 11, r: 7 }),
            h('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
          ),
          h('input', {
            type: 'text',
            className: 'ts-search-input',
            placeholder: 'Search',
            value: search,
            onChange: function(e) { setSearch(e.target.value); },
            'aria-label': 'Search templates'
          }),
          search
            ? h('button', {
                type: 'button',
                className: 'ts-search-clear',
                onClick: function() { setSearch(''); },
                'aria-label': 'Clear search'
              }, '×')
            : null
        ),
        h('div', { className: 'ts-sort' },
          h('select', {
            className: 'ts-sort-select',
            value: sort,
            onChange: function(e) { setSort(e.target.value); },
            'aria-label': 'Sort templates'
          },
            h('option', { value: 'popular' }, 'Most popular'),
            h('option', { value: 'newest' }, 'Newest'),
            h('option', { value: 'az' }, 'A–Z')
          ),
          h('span', { className: 'ts-sort-caret', 'aria-hidden': true }, '▾')
        )
      )
    ),
    loading ? h('div', { className: 'ts-state' }, 'Loading templates…') : null,
    error ? h('div', { className: 'ts-state ts-state--error' }, error) : null,
    !loading && !error && !rows.length && !showFeatured
      ? h('div', { className: 'ts-state' }, emptyMessage)
      : null,
    h('div', { className: 'ts-rows' },
      rows.map(function(t) {
        return h(TemplateRow, { key: t.id, template: t, onOpen: props.onOpen });
      })
    )
  );
}

function TemplateStorePage(props) {
  var onOpen = function(id) { props.onNavigateTo('/store/' + encodeURIComponent(id)); };
  var go = function(path) { return function(e) { if (e) e.preventDefault(); props.onNavigateTo(path); }; };
  return h('div', { className: 'ts-page' },
    h('div', { className: 'ts-topbar' },
      h('div', { className: 'ts-nav-left' },
        h('button', { type: 'button', className: 'ts-logo', onClick: go(props.currentUser ? '/dashboard' : '/') },
          // Official AlleFlow brand mark (mirrors BrandLogo in App.js): dark
          // rounded square with the node glyph (dot · line · arrow).
          h('svg', { className: 'ts-logo-mark', width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true },
            h('rect', { x: 0, y: 0, width: 24, height: 24, rx: 6, fill: '#1a1f2e' }),
            h('circle', { cx: 8, cy: 12, r: 2.5, fill: 'white', fillOpacity: 0.9 }),
            h('line', { x1: 10.5, y1: 12, x2: 13.5, y2: 12, stroke: 'white', strokeWidth: 1.5, strokeOpacity: 0.65 }),
            h('path', { d: 'M13 9 L17 12 L13 15', stroke: 'white', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' })
          ),
          h('span', { className: 'ts-logo-name' }, 'AlleFlow')
        ),
        h('nav', { className: 'ts-nav' },
          h('a', { href: '/store', className: 'ts-nav-link ts-nav-link--active', onClick: go('/store') }, 'Templates'),
          h('a', { href: '/apidocs', className: 'ts-nav-link', onClick: go('/apidocs') }, 'Docs')
        )
      ),
      h('button', { type: 'button', className: 'ts-home', onClick: go(props.currentUser ? '/dashboard' : '/') },
        props.currentUser ? '← Dashboard' : '← Home')
    ),
    props.templateId
      ? h(StoreDetail, {
          templateId: props.templateId,
          currentUser: props.currentUser,
          onNavigateTo: props.onNavigateTo,
          onBack: function() { props.onNavigateTo('/store'); },
          onInstalled: function(id) { if (id) props.onNavigateTo('/workflow/' + encodeURIComponent(id)); }
        })
      : h(StoreGrid, { onOpen: onOpen })
  );
}

module.exports = TemplateStorePage;
